/**
 * Thin fetch wrapper.
 *
 * The API is same-origin (the SPA is served from Aufmass.Api/wwwroot), so cookies travel
 * without any CORS or credentials dance. In dev, Vite proxies /api to :5183.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the session has expired or was never established. */
  get isUnauthorized() {
    return this.status === 401
  }

  /** True when the user is logged in but their LoginType lacks the capability. */
  get isForbidden() {
    return this.status === 403
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  if (!res.ok) {
    // The API returns ProblemDetails; fall back to the status text if the body is not JSON
    // (a proxy error, for instance).
    let title = res.statusText
    let detail: string | undefined
    try {
      const problem = await res.json()
      title = problem.title ?? title
      detail = problem.detail
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, title, detail)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    // Omit `body` entirely rather than passing undefined: exactOptionalPropertyTypes
    // distinguishes "absent" from "present and undefined", and RequestInit means the former.
    request<T>(path, body === undefined
      ? { method: 'POST' }
      : { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined
      ? { method: 'PUT' }
      : { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export interface Me {
  userId: string
  name: string
  email: string
  loginType: number
  isAdmin: boolean
  capabilities: string[]
}

export const Capabilities = {
  ViewOperations: 'CanViewOperations',
  ViewAufmassGrid: 'CanViewAufmassGrid',
  ViewRtk: 'CanViewRtk',
  EditAufmass: 'CanEditAufmass',
  ViewFinancials: 'CanViewFinancials',
  ViewReconciliation: 'CanViewReconciliation',
  AdminRefresh: 'CanAdminRefresh',
  ViewDashboard: 'CanViewDashboard',
  ViewProjects: 'CanViewProjects',
  ViewBonus: 'CanViewBonus',
  ManageUsers: 'CanManageUsers',
  ViewInventory: 'CanViewInventory',
  MoveInventory: 'CanMoveInventory',
  ViewFleet: 'CanViewFleet',
  MoveFleet: 'CanMoveFleet',
  ViewBilling: 'CanViewBilling',
  ManageBilling: 'CanManageBilling',
  ViewPeople: 'CanViewPeople',
} as const

export type Capability = (typeof Capabilities)[keyof typeof Capabilities]
